import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import { Layout } from "../Layouts/Layout";
import { ResponseCard } from "../Components/Response/Card";
import { SubmissionReviewTaskCard } from "../Components/SubmissionReview/TaskCard";
import {
  GET_MY_RESPONSES,
  MyResponsesResult,
  GET_MY_REVIEW_TASKS,
  GET_MY_SUBMISSION_REVIEW_TASKS,
} from "../Queries";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { useErrorBoundary } from "../Contexts/ErrorContext";

interface Props {
  /** FL-284: the /surveys and /submissions routes rendered identical,
   * unfiltered lists — each now shows only its own kind. */
  variant?: "surveys" | "submissions";
}

export const OpenResponses = ({ variant }: Props) => {
  const { setError } = useErrorBoundary();
  const [t] = useTranslation();
  const showSurveys = variant !== "submissions";
  const showSubmissions = variant !== "surveys";

  //
  // Queries
  //

  const { loading: responsesLoading, data: responsesData } =
    useQuery<MyResponsesResult>(GET_MY_RESPONSES, {
      variables: {
        filters: {},
      },
      onError: (error) => setError(t("errors.responses.currentUser"), error),
    });

  const { loading: tasksLoading, data: tasksData } = useQuery(
    GET_MY_SUBMISSION_REVIEW_TASKS,
    {
      onError: () => {
        // Silent fail — submission review tasks are supplementary
      },
    },
  );

  // FL-285: reviews the student still owes get their own CTA card
  const { data: reviewTasksData } = useQuery(GET_MY_REVIEW_TASKS, {
    onError: () => {
      // Silent fail — review tasks are supplementary
    },
  });

  const responses = responsesData ? responsesData.responses : [];
  // FL-337: live presentations never have a real submission — the
  // auto-submitted record is only a review target (FL-301), so a
  // "Submitted" card would be misleading. The results-released card stays:
  // it's the student's only in-app path to those results (FL-284).
  const submissionTasks = (tasksData?.mySubmissionReviewTasks ?? []).filter(
    (task: any) =>
      task.activitySubmissionType !== "LIV" ||
      (task.activityStatus === "RL" && task.resultsLink),
  );
  const reviewTasks = reviewTasksData?.myReviewAllocations ?? [];

  const sortedResponses = [...responses].sort((responseA, responseB) => {
    const nonActionableStates = ["CL", "AR", "SC", "ACS"];
    const openOrder =
      (responseA.submitted ||
      nonActionableStates.includes(responseA.campaignState)
        ? 1
        : 0) -
      (responseB.submitted ||
      nonActionableStates.includes(responseB.campaignState)
        ? 1
        : 0);
    const createdOrder =
      (responseA.campaignDueDate
        ? new Date(responseA.campaignDueDate).getTime()
        : 0) -
      (responseB.campaignDueDate
        ? new Date(responseB.campaignDueDate).getTime()
        : 0);
    return openOrder == 0 ? createdOrder : openOrder;
  });

  // Sort submission tasks: pending first, then by due date
  const sortedSubmissionTasks = [...submissionTasks].sort((a: any, b: any) => {
    const aPending = a.status !== "SB" && a.activityStatus === "SO" ? 0 : 1;
    const bPending = b.status !== "SB" && b.activityStatus === "SO" ? 0 : 1;
    if (aPending !== bPending) return aPending - bPending;
    const aDate = a.activityDueDate ? new Date(a.activityDueDate).getTime() : 0;
    const bDate = b.activityDueDate ? new Date(b.activityDueDate).getTime() : 0;
    return aDate - bDate;
  });

  const showLoading = responsesLoading && !responsesData;

  return (
    <Layout
      breadcrumbs={[{ title: t("breadcrumbs.yourSurveys") }]}
      loading={showLoading}
      studentVariant
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <div></div>
      </Box>

      {/* Submission review tasks */}
      {showSubmissions &&
        (sortedSubmissionTasks.length > 0 || reviewTasks.length > 0) && (
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              color="primary"
              sx={{ fontWeight: "bold", mb: 2 }}
            >
              {t("Submission Reviews")}
            </Typography>
            <Grid container spacing={2}>
              {sortedSubmissionTasks.map((task: any) => (
                <Grid item key={task.id} xs={12} md={6} lg={4}>
                  <SubmissionReviewTaskCard
                    type="submission"
                    submission={task}
                  />
                </Grid>
              ))}
              {/* FL-285: CTA cards for reviews the student still owes */}
              {reviewTasks.map((allocation: any) => (
                <Grid item key={allocation.id} xs={12} md={6} lg={4}>
                  <SubmissionReviewTaskCard type="review" review={allocation} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

      {/* Survey responses */}
      {showSurveys && (
        <Grid container spacing={2}>
          {sortedResponses.map((response) => (
            <Grid item key={response.id} xs={12} md={6} lg={4}>
              <ResponseCard response={response} />
            </Grid>
          ))}
        </Grid>
      )}
    </Layout>
  );
};
